import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Divider,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar, // 👈 ADDED
  Alert     // 👈 ADDED
} from "@mui/material";

import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LogoutIcon from "@mui/icons-material/Logout";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import { getDeliveryProfile } from "../api/deliveryApi";
import { useContext } from "react";
import { AuthContext } from "../context/AuthContext";

export default function DeliveryAccount() {
  const { logout } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  // 👈 ADDED SNACKBAR STATE
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await getDeliveryProfile();
      setProfile(res.data);
    } catch (err) {
      console.log(err);
      // 👈 REPLACED ALERT WITH SNACKBAR
      setSnackbar({
        open: true,
        message: "Failed to load profile",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleOpenLogout = () => {
    setOpenLogoutDialog(true);
  };

  const handleCloseLogout = () => {
    setOpenLogoutDialog(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#FFFDF8",
        }}
      >
        <CircularProgress sx={{ color: "#E85D3F" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: 2,
        pt: { xs: 10, sm: 12 }, 
        pb: { xs: 10, sm: 12 }, 
        bgcolor: "#FFFDF8",
        minHeight: "100vh",
      }}
    >
      <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23", mb: 3, ml: 1 }}>
        My Account
      </Typography>

      <Card
        elevation={0}
        sx={{
          borderRadius: "24px",
          mb: 3,
          border: "1px solid #ECE5DA",
          boxShadow: "0 8px 32px rgba(58, 44, 35, 0.08)",
          bgcolor: "#FFFFFF",
          overflow: "visible",
        }}
      >
        <CardContent sx={{ pt: 3, px: 3, pb: "24px !important" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2.5,
              mb: 3,
            }}
          >
            <Avatar
              sx={{
                width: 76,
                height: 76,
                bgcolor: "#E85D3F",
                fontSize: 32,
                fontWeight: 800,
                border: "4px solid #FFFDF8",
                boxShadow: "0 4px 16px rgba(232, 93, 63, 0.25)",
              }}
            >
              {profile?.name?.charAt(0)?.toUpperCase() || "?"}
            </Avatar>

            <Box>
              <Typography
                variant="h5"
                fontWeight="900"
                sx={{ color: "#3A2C23", lineHeight: 1.2, mb: 0.5 }}
              >
                {profile?.name || "Unknown User"}
              </Typography>
              <Typography variant="body2" fontWeight="700" sx={{ color: "#A59D96" }}>
                Delivery Partner
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 1, borderColor: "#ECE5DA" }} />

          <List disablePadding>
            <ListItem disablePadding sx={{ py: 2 }}>
              <ListItemIcon sx={{ minWidth: 52 }}>
                <Box sx={{ bgcolor: "rgba(52, 168, 83, 0.12)", p: 1.2, borderRadius: "12px", display: "flex" }}>
                  <PhoneIcon sx={{ color: "#34A853", fontSize: 22 }} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography fontWeight="800" variant="caption" sx={{ color: "#A59D96", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Phone Number
                  </Typography>
                }
                secondary={
                  <Typography variant="body1" fontWeight="700" sx={{ color: "#3A2C23", mt: 0.3 }}>
                    {profile?.phone || "N/A"}
                  </Typography>
                }
              />
            </ListItem>

            <Divider component="li" sx={{ borderColor: "#F2EEE8" }} />

            <ListItem disablePadding sx={{ py: 2 }}>
              <ListItemIcon sx={{ minWidth: 52 }}>
                <Box sx={{ bgcolor: "rgba(248, 195, 50, 0.15)", p: 1.2, borderRadius: "12px", display: "flex" }}>
                  <EmailIcon sx={{ color: "#D9A01C", fontSize: 22 }} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography fontWeight="800" variant="caption" sx={{ color: "#A59D96", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Email Address
                  </Typography>
                }
                secondary={
                  <Typography variant="body1" fontWeight="700" sx={{ color: "#3A2C23", mt: 0.3 }}>
                    {profile?.email || "N/A"}
                  </Typography>
                }
              />
            </ListItem>

            <Divider component="li" sx={{ borderColor: "#F2EEE8" }} />

            <ListItem disablePadding sx={{ py: 2 }}>
              <ListItemIcon sx={{ minWidth: 52 }}>
                <Box sx={{ bgcolor: "rgba(66, 133, 244, 0.12)", p: 1.2, borderRadius: "12px", display: "flex" }}>
                  <LocationOnIcon sx={{ color: "#4285F4", fontSize: 22 }} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography fontWeight="800" variant="caption" sx={{ color: "#A59D96", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Service Zone
                  </Typography>
                }
                secondary={
                  <Typography variant="body1" fontWeight="700" sx={{ color: "#3A2C23", mt: 0.3 }}>
                    {profile?.city && profile?.zone
                      ? `${profile.city} • ${profile.zone}`
                      : "N/A"}
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Button
        fullWidth
        variant="contained"
        disableElevation
        startIcon={<LogoutIcon />}
        onClick={handleOpenLogout}
        sx={{
          py: 1.8,
          borderRadius: "16px",
          bgcolor: "#FF4B2B",
          color: "#FFFFFF",
          fontWeight: "900",
          fontSize: "1.05rem",
          textTransform: "none",
          boxShadow: "0 8px 24px rgba(255, 75, 43, 0.25)",
          "&:hover": { bgcolor: "#E03E23" },
        }}
      >
        Logout
      </Button>

      <Dialog
        open={openLogoutDialog}
        onClose={handleCloseLogout}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            p: 1,
            boxShadow: "0 16px 40px rgba(58, 44, 35, 0.15)",
          }
        }}
      >
        <DialogTitle fontWeight="900" sx={{ color: "#3A2C23", fontSize: "1.3rem" }}>
          Log Out
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ color: "#6E655E", fontWeight: 600 }}>
            Are you sure you want to log out of your delivery account?
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: "flex", gap: 1.5 }}>
          <Button
            onClick={handleCloseLogout}
            variant="outlined"
            sx={{
              flex: 1,
              color: "#6E655E",
              borderColor: "#ECE5DA",
              fontWeight: 800,
              textTransform: "none",
              borderRadius: "12px",
              py: 1.2,
              "&:hover": { borderColor: "#A59D96", bgcolor: "transparent" }
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleLogout}
            variant="contained"
            disableElevation
            sx={{
              flex: 1,
              bgcolor: "#FF4B2B",
              color: "#FFFFFF",
              borderRadius: "12px",
              fontWeight: 800,
              textTransform: "none",
              py: 1.2,
              "&:hover": { bgcolor: "#E03E23" }
            }}
          >
            Log Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* 👈 ADDED TOP SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: { xs: 20, sm: 24 } }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "12px", fontWeight: "bold", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}